<template>
    <div v-if="visible" class="assistant-panel" :style="{ width: width + 'px' }">
        <div
            class="assistant-resize-handle"
            title="Потяните, чтобы изменить ширину панели"
            @mousedown="startResize"
        ></div>
        <div class="assistant-header">
            <span class="assistant-title">
                <i class="pi pi-android"></i> AI-помощник
            </span>
            <div class="assistant-header-buttons">
                <PrimeButton
                    icon="pi pi-trash"
                    class="p-button-text p-button-rounded p-button-sm"
                    title="Очистить историю диалога"
                    @click="clearHistory"
                />
                <PrimeButton
                    icon="pi pi-times"
                    class="p-button-text p-button-rounded p-button-sm"
                    title="Закрыть"
                    @click="$emit('close')"
                />
            </div>
        </div>

        <div ref="messagesArea" class="assistant-messages">
            <div v-if="messages.length === 0 && !loading" class="assistant-empty">
                <p>Помогу подготовить диплом: придумаю текст, соберу макет, сгенерирую фон. Можно приложить картинку-референс для фона.</p>
                <div class="assistant-quick-prompts">
                    <button
                        v-for="prompt in quickPrompts"
                        :key="prompt"
                        class="assistant-quick-prompt"
                        @click="useQuickPrompt(prompt)"
                    >{{ prompt }}</button>
                </div>
            </div>

            <template v-for="(message, index) in messages" :key="index">
                <div
                    class="assistant-message"
                    :class="'assistant-message-' + message.role"
                >
                    <div
                        v-if="message.role === 'assistant'"
                        class="assistant-bubble assistant-bubble-md"
                        v-show="message.content"
                        v-html="renderMarkdown(message.content)"
                    ></div>
                    <div v-else class="assistant-bubble">
                        <img
                            v-for="(attachment, attachIndex) in message.attachments || []"
                            :key="'attach-' + attachIndex"
                            :src="attachment.url"
                            class="assistant-attachment-img"
                            alt="Референс фона"
                        />
                        {{ message.content }}
                    </div>
                    <div
                        v-if="message.actions && message.actions.length"
                        class="assistant-actions"
                    >
                        <div
                            v-for="action in candidateActions(message.actions)"
                            :key="'candidate-' + action.candidate_id"
                            class="assistant-candidate"
                        >
                            <img :src="action.preview_url" class="assistant-action-preview" alt="Кандидат фона" />
                            <div class="assistant-candidate-buttons">
                                <PrimeButton
                                    v-if="candidateStatus(action) === 'candidate'"
                                    label="Применить"
                                    icon="pi pi-check"
                                    class="p-button-sm"
                                    :disabled="busyCandidate === action.candidate_id"
                                    @click="applyCandidate(action)"
                                />
                                <PrimeButton
                                    label="Ещё вариант"
                                    icon="pi pi-refresh"
                                    class="p-button-sm p-button-outlined"
                                    :disabled="busyCandidate === action.candidate_id"
                                    @click="createVariation(action)"
                                />
                                <PrimeButton
                                    v-if="candidateStatus(action) === 'candidate'"
                                    icon="pi pi-trash"
                                    class="p-button-sm p-button-text p-button-danger"
                                    title="Удалить кандидат"
                                    :disabled="busyCandidate === action.candidate_id"
                                    @click="deleteCandidate(action)"
                                />
                            </div>
                            <small v-if="candidateStatus(action) === 'active'">Фон применён</small>
                            <small v-else-if="candidateStatus(action) === 'archived'">Версия находится в истории</small>
                            <small v-if="action.candidateError" class="assistant-candidate-error">{{ action.candidateError }}</small>
                        </div>
                        <img
                            v-for="(url, previewIndex) in plainPreviewUrls(message.actions)"
                            :key="'preview-' + previewIndex"
                            :src="url"
                            class="assistant-action-preview"
                            alt="Фон"
                        />
                        <button
                            v-if="successfulActions(message.actions).length"
                            type="button"
                            class="assistant-actions-toggle"
                            @click="toggleActions(index)"
                        >
                            <i :class="expandedActions[index] ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></i>
                            Выполнено действий: {{ successfulActions(message.actions).length }}
                        </button>
                        <template v-for="(action, actionIndex) in message.actions" :key="actionIndex">
                            <div
                                v-if="action.status !== 'ok' || expandedActions[index]"
                                class="assistant-action"
                                :class="'assistant-action-' + action.status"
                            >
                                <i :class="actionIcon(action)"></i>
                                <span>{{ action.summary }}</span>
                                <span
                                    v-if="action.status === 'ok' && action.reference_source"
                                    class="assistant-action-ref-source"
                                >{{ referenceSourceLabel(action.reference_source) }}</span>
                            </div>
                        </template>
                    </div>
                </div>
            </template>

            <div v-if="latestArchived" class="assistant-background-history">
                <img :src="latestArchived.preview_url" alt="Предыдущий фон" />
                <span>Предыдущий фон</span>
                <PrimeButton
                    label="Отменить применение"
                    class="p-button-sm p-button-text"
                    :disabled="busyCandidate === latestArchived.id"
                    @click="restoreVersion(latestArchived)"
                />
            </div>

            <div v-if="loading" class="assistant-status">
                <i class="pi pi-spin pi-spinner"></i>
                <span>{{ status || 'Ассистент думает…' }}</span>
            </div>
        </div>

        <div class="assistant-input">
            <div v-if="referencePreview" class="assistant-attachment-chip">
                <img :src="referencePreview" class="assistant-attachment-thumb" alt="Референс" />
                <span class="assistant-attachment-label">Референс</span>
                <PrimeButton
                    icon="pi pi-times"
                    class="p-button-text p-button-rounded p-button-sm"
                    title="Убрать референс"
                    @click="clearReference()"
                />
            </div>
            <div class="assistant-input-row">
                <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    class="assistant-file-input"
                    @change="onFileChange"
                />
                <PrimeButton
                    icon="pi pi-paperclip"
                    class="p-button-text p-button-rounded assistant-attach"
                    title="Приложить референс фона"
                    :disabled="loading"
                    @click="$refs.fileInput.click()"
                />
                <PrimeTextArea
                    v-model="input"
                    :autoResize="true"
                    rows="1"
                    placeholder="Например: собери диплом победителя олимпиады"
                    class="assistant-textarea"
                    @keydown.enter.exact.prevent="send"
                />
                <PrimeButton
                    icon="pi pi-send"
                    class="p-button-rounded assistant-send"
                    :disabled="loading || !input.trim()"
                    @click="send"
                />
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/libs/api";
import { renderMarkdown } from "@/libs/markdown";

const TEMPLATE_TOOLS = ['add_text_block', 'update_text_block', 'delete_block'];
const DATA_TOOLS = ['create_data_keys', 'add_data_keys', 'add_data_row'];

// Подписи для статусной строки, пока ассистент выполняет инструменты
const TOOL_LABELS = {
    get_project_state: 'Смотрю состояние проекта…',
    list_fonts: 'Проверяю доступные шрифты…',
    add_text_block: 'Добавляю текстовый блок…',
    update_text_block: 'Правлю текстовый блок…',
    delete_block: 'Удаляю блок…',
    create_data_keys: 'Создаю поля данных…',
    add_data_keys: 'Добавляю поля данных…',
    add_data_row: 'Добавляю строку данных…',
    get_data_sample: 'Смотрю данные проекта…',
    generate_background: 'Генерирую фон — это может занять до минуты…',
    edit_background: 'Правлю фон — это может занять до минуты…',
};

// Подписи для reference_source в карточке действия
const REFERENCE_SOURCE_LABELS = {
    current_message: 'по референсу из сообщения',
    latest_conversation: 'по последнему референсу диалога',
    current_background: 'на основе текущего фона',
    none: 'без референса',
};

// Ширина панели настраивается drag'ом за левый край и запоминается
const WIDTH_STORAGE_KEY = 'assistant-panel.width';
const DEFAULT_WIDTH = 480;
const MIN_WIDTH = 360;

export default {
    props: {
        project: {
            type: Object,
            required: true,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            messages: [],
            input: '',
            loading: false,
            status: '',
            expandedActions: {},
            width: DEFAULT_WIDTH,
            historyLoaded: false,
            referenceFile: null,
            referencePreview: null,
            quickPrompts: [
                'Собери макет диплома победителя конкурса',
                'Придумай текст для грамоты',
                'Сгенерируй фон для диплома',
                'Проверь wildcard\'ы на шаблоне',
            ],
            backgroundVersions: [],
            busyCandidate: null,
        };
    },
    created() {
        const saved = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
        if (saved >= MIN_WIDTH) {
            this.width = saved;
        }
    },
    mounted() {
        if (this.visible) this.$emit('resize', this.width);
    },
    beforeUnmount() {
        this.$emit('resize', 0);
    },
    methods: {
        renderMarkdown,
        loadHistory() {
            if (!this.project.id) return;
            api.getAiHistory(this.project.id).then(data => {
                this.messages = (data.messages || []).map(message => ({
                    role: message.role,
                    content: message.content,
                    actions: message.actions || [],
                    attachments: message.attachments || [],
                }));
                this.historyLoaded = true;
                this.loadBackgroundVersions();
                this.scrollToBottom();
            }).catch(() => {
                this.historyLoaded = true;
                this.loadBackgroundVersions();
            });
        },
        useQuickPrompt(prompt) {
            this.input = prompt;
            this.send();
        },
        onFileChange(event) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;
            this.clearReference();
            this.referenceFile = file;
            this.referencePreview = URL.createObjectURL(file);
        },
        clearReference(revoke = true) {
            if (revoke && this.referencePreview) {
                URL.revokeObjectURL(this.referencePreview);
            }
            this.referenceFile = null;
            this.referencePreview = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
        send() {
            const text = this.input.trim();
            if (!text || this.loading) return;

            const attachments = this.referencePreview ? [{url: this.referencePreview}] : [];
            const file = this.referenceFile;
            this.messages.push({role: 'user', content: text, actions: [], attachments});
            this.input = '';
            // Превью остаётся в эхо-пузыре, поэтому object URL не отзываем.
            this.clearReference(false);

            // Пузырь ответа создаём сразу и наполняем по мере стрима
            this.messages.push({role: 'assistant', content: '', actions: [], attachments: []});
            const messageIndex = this.messages.length - 1;
            const assistantMessage = () => this.messages[messageIndex];

            this.loading = true;
            this.status = '';
            this.scrollToBottom();

            api.sendAiMessage(this.project.id, text, file, (event) => {
                if (event.type === 'status') {
                    this.status = TOOL_LABELS[event.tool] || 'Выполняю действие…';
                } else if (event.type === 'action') {
                    assistantMessage().actions.push(event.action);
                    this.status = '';
                } else if (event.type === 'delta') {
                    assistantMessage().content += event.text;
                }
                this.scrollToBottom();
            }).then(done => {
                assistantMessage().content = done.reply;
                assistantMessage().actions = done.actions || [];
                this.notifyApplied(assistantMessage().actions);
            }).catch(err => {
                const errorText = err.response?.data?.message || err.message
                    || 'Не удалось получить ответ ассистента. Попробуйте ещё раз.';
                const message = assistantMessage();
                message.content = message.content ? message.content + '\n\n' + errorText : errorText;
            }).finally(() => {
                this.loading = false;
                this.status = '';
                this.scrollToBottom();
            });
        },
        notifyApplied(actions) {
            const successful = actions.filter(action => action.status === 'ok');
            if (!successful.length) return;

            const affectsTemplate = successful.some(action => TEMPLATE_TOOLS.includes(action.tool));
            const affectsData = successful.some(action => DATA_TOOLS.includes(action.tool));
            if (affectsTemplate || affectsData) {
                this.$emit('actions-applied', {affectsTemplate, affectsData});
            }
        },
        loadBackgroundVersions() {
            if (!this.project.id) return;
            api.getBackgroundVersions(this.project.id).then(data => {
                this.backgroundVersions = data.assets || [];
            }).catch(() => {});
        },
        applyCandidate(action) {
            this.busyCandidate = action.candidate_id;
            action.candidateError = '';
            api.applyBackground(this.project.id, action.candidate_id).then(() => {
                action.candidateState = 'applied';
                this.$emit('actions-applied', {affectsTemplate: true, affectsData: false});
                this.loadBackgroundVersions();
            }).catch(err => {
                action.candidateError = err.response?.data?.message || 'Не удалось применить фон.';
            }).finally(() => {
                this.busyCandidate = null;
            });
        },
        createVariation(action) {
            this.busyCandidate = action.candidate_id;
            action.candidateError = '';
            api.createBackgroundVariation(this.project.id, action.candidate_id).then(data => {
                const asset = data.asset;
                this.messages.push({
                    role: 'assistant',
                    content: 'Создан ещё один вариант с тем же дизайном.',
                    attachments: [],
                    actions: [{
                        tool: 'generate_background',
                        status: 'ok',
                        summary: 'Новый вариант готов к просмотру',
                        candidate_id: asset.id,
                        preview_url: asset.preview_url,
                    }],
                });
                this.loadBackgroundVersions();
            }).catch(err => {
                action.candidateError = err.response?.data?.message || 'Не удалось создать вариант.';
            }).finally(() => {
                this.busyCandidate = null;
                this.scrollToBottom();
            });
        },
        deleteCandidate(action) {
            this.busyCandidate = action.candidate_id;
            action.candidateError = '';
            api.deleteBackgroundCandidate(this.project.id, action.candidate_id).then(() => {
                action.candidateState = 'deleted';
                action.preview_url = null;
                this.loadBackgroundVersions();
            }).catch(err => {
                action.candidateError = err.response?.data?.message || 'Не удалось удалить кандидат.';
            }).finally(() => {
                this.busyCandidate = null;
            });
        },
        restoreVersion(version) {
            this.busyCandidate = version.id;
            api.restoreBackground(this.project.id, version.id).then(() => {
                this.$emit('actions-applied', {affectsTemplate: true, affectsData: false});
                this.loadBackgroundVersions();
            }).finally(() => {
                this.busyCandidate = null;
            });
        },
        clearHistory() {
            api.clearAiHistory(this.project.id).then(() => {
                this.messages = [];
                this.expandedActions = {};
            });
        },
        toggleActions(index) {
            this.expandedActions[index] = !this.expandedActions[index];
        },
        startResize(event) {
            event.preventDefault();
            const startX = event.clientX;
            const startWidth = this.width;
            const maxWidth = Math.round(window.innerWidth * 0.7);
            const onMove = (moveEvent) => {
                const next = startWidth + (startX - moveEvent.clientX);
                this.width = Math.max(MIN_WIDTH, Math.min(maxWidth, next));
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                localStorage.setItem(WIDTH_STORAGE_KEY, String(this.width));
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        successfulActions(actions) {
            return (actions || []).filter(action => action.status === 'ok');
        },
        actionIcon(action) {
            if (action.status !== 'ok') return 'pi pi-exclamation-triangle';
            switch (action.tool) {
                case 'generate_background':
                case 'edit_background': return 'pi pi-image';
                case 'delete_block': return 'pi pi-trash';
                case 'create_data_keys':
                case 'add_data_keys':
                case 'add_data_row': return 'pi pi-table';
                default: return 'pi pi-check-circle';
            }
        },
        referenceSourceLabel(source) {
            return REFERENCE_SOURCE_LABELS[source] || '';
        },
        candidateActions(actions) {
            return (actions || []).filter(action =>
                action.status === 'ok'
                && action.preview_url
                && action.candidate_id
                && action.candidateState !== 'deleted'
                && (!this.backgroundVersions.length || this.backgroundVersions.some(asset => asset.id === action.candidate_id))
            );
        },
        candidateStatus(action) {
            if (action.candidateState === 'applied') return 'active';
            const asset = this.backgroundVersions.find(item => item.id === action.candidate_id);
            return asset?.status || 'candidate';
        },
        plainPreviewUrls(actions) {
            return (actions || [])
                .filter(action => action.status === 'ok' && action.preview_url && !action.candidate_id)
                .map(action => action.preview_url);
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const area = this.$refs.messagesArea;
                if (area) area.scrollTop = area.scrollHeight;
            });
        },
    },
    watch: {
        visible(value) {
            if (value && !this.historyLoaded) this.loadHistory();
            this.$emit('resize', value ? this.width : 0);
        },
        width(value) {
            if (this.visible) this.$emit('resize', value);
        },
        'project.id'() {
            this.messages = [];
            this.expandedActions = {};
            this.historyLoaded = false;
            this.backgroundVersions = [];
            this.clearReference();
            if (this.visible) this.loadHistory();
        },
    },
    computed: {
        latestArchived() {
            return this.backgroundVersions.find(asset => asset.status === 'archived') || null;
        },
    },
};
</script>

<style lang="scss" scoped>
.assistant-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    // Ширина задаётся инлайн-стилем и меняется drag'ом за левый край
    background: #ffffff;
    border-left: 1px solid #e2e8f0;
    box-shadow: -4px 0 16px rgba(15, 23, 42, 0.12);
    display: flex;
    flex-direction: column;
    z-index: 1100;
}

.assistant-resize-handle {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    cursor: col-resize;
    z-index: 1;

    &:hover {
        background: #bfdbfe;
    }
}

.assistant-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.6rem 0.75rem;
    border-bottom: 1px solid #e2e8f0;
}

.assistant-title {
    font-weight: 600;
    font-size: 1.05rem;
}

.assistant-messages {
    flex: 1;
    overflow-y: auto;
    padding: 0.75rem;
}

.assistant-empty {
    color: #64748b;
    font-size: 0.95rem;
}

.assistant-quick-prompts {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-top: 0.6rem;
}

.assistant-quick-prompt {
    text-align: left;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.45rem 0.6rem;
    cursor: pointer;
    font-size: 0.9rem;

    &:hover {
        background: #e2e8f0;
    }
}

.assistant-message {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.6rem;
}

.assistant-message-user {
    align-items: flex-end;
}

.assistant-message-assistant {
    align-items: flex-start;
}

.assistant-bubble {
    max-width: 85%;
    padding: 0.5rem 0.7rem;
    border-radius: 12px;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.95rem;
}

.assistant-message-user .assistant-bubble {
    background: #3b82f6;
    color: #ffffff;
    border-bottom-right-radius: 4px;
}

.assistant-message-assistant .assistant-bubble {
    background: #f1f5f9;
    color: #0f172a;
    border-bottom-left-radius: 4px;
}

.assistant-status {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    color: #64748b;
    font-size: 0.85rem;
    padding: 0.15rem 0.2rem;
}

.assistant-bubble-md {
    white-space: normal;

    :deep(.md-header) {
        font-weight: 600;
        margin: 0.35rem 0 0.1rem;
    }

    :deep(ul),
    :deep(ol) {
        margin: 0.25rem 0;
        padding-left: 1.3rem;
    }

    :deep(code) {
        font-family: monospace;
        font-size: 0.86em;
        background: #e2e8f0;
        border-radius: 4px;
        padding: 0 0.25rem;
    }

    :deep(pre) {
        background: #e2e8f0;
        border-radius: 6px;
        padding: 0.4rem 0.55rem;
        margin: 0.3rem 0;
        overflow-x: auto;

        code {
            background: transparent;
            padding: 0;
        }
    }

    :deep(.md-table) {
        border-collapse: collapse;
        margin: 0.3rem 0;
        font-size: 0.85rem;

        th,
        td {
            border: 1px solid #cbd5e1;
            padding: 0.25rem 0.5rem;
            text-align: left;
            vertical-align: top;
        }

        th {
            background: #e2e8f0;
            font-weight: 600;
        }
    }

    :deep(a) {
        color: #2563eb;
        text-decoration: underline;
    }
}

.assistant-attachment-img {
    display: block;
    max-width: 100%;
    border-radius: 8px;
    margin-bottom: 0.35rem;
}

.assistant-actions {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.35rem;
    max-width: 85%;
}

.assistant-actions-toggle {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    align-self: flex-start;
    background: transparent;
    border: none;
    padding: 0.1rem 0;
    cursor: pointer;
    font-size: 0.8rem;
    color: #94a3b8;

    &:hover {
        color: #64748b;
    }
}

.assistant-action {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    padding: 0.3rem 0.5rem;
    border-radius: 8px;
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
}

.assistant-action-error {
    background: #fef2f2;
    color: #b91c1c;
    border-color: #fecaca;
}

.assistant-action-ref-source {
    font-size: 0.76rem;
    color: #94a3b8;
    margin-left: auto;
    white-space: nowrap;
}

.assistant-action-preview {
    max-width: 100%;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.assistant-candidate {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.assistant-candidate-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.assistant-candidate-error {
    color: #b91c1c;
}

.assistant-background-history {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin: 0.5rem 0;
    padding: 0.4rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.8rem;
}

.assistant-background-history img {
    width: 52px;
    height: 38px;
    object-fit: cover;
    border-radius: 4px;
}

.assistant-input {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding: 0.6rem 0.75rem;
    border-top: 1px solid #e2e8f0;
}

.assistant-input-row {
    display: flex;
    align-items: flex-end;
    gap: 0.4rem;
}

.assistant-file-input {
    display: none;
}

.assistant-attachment-chip {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.3rem 0.4rem;
}

.assistant-attachment-thumb {
    max-width: 56px;
    max-height: 40px;
    border-radius: 4px;
}

.assistant-attachment-label {
    flex: 1;
    font-size: 0.82rem;
    color: #64748b;
}

.assistant-textarea {
    flex: 1;
    max-height: 140px;
}
</style>
